import type { components } from '@/generated/contracts/api-v1';
import { apiPath, changeStream } from './routing';

/**
 * Fewer requests per screen load:
 * - An identical read already on its way is answered with its answer, so parts of a page that read the same thing as
 *   they mount (the membership, a scene, the user's feedback) make one request.
 * - The editor opening a scene reads it with one getEditorWorkspace, which answers the reads its parts make as they
 *   mount: the scene, its story and project, its content and the lists of its placements, clips, keyframes and screen
 *   messages, its materials, effects and health, the material presets, shader looks and publishing matrix, the caller's
 *   guidance states, and the library lists the editor preloads.
 * - The app starting for a signed-in user reads one getSessionBootstrap, which answers the user, their membership and
 *   org, the environment's preferences, the remote config and the version.
 * A read is answered from one of these only where it holds exactly what that read would answer, only for the user it
 * was read for, and only until something that could change it is written, a change from elsewhere comes in, or
 * OPENING_MS passes. The screen reads are made only as the editor opens a scene and as the app starts, so a read later,
 * or on another page, is its own read. A read sent after a write, or after a change from elsewhere, never shares one
 * sent before it, and another user signing in starts from nothing.
 */

type Schemas = components['schemas'];
type EditorWorkspace = Schemas['EditorWorkspaceDto'];
type SceneContent = Schemas['SceneContentDto'];
type SessionBootstrap = Schemas['SessionBootstrapDto'];
type Send = (request: Request) => Promise<Response>;
type Row = Record<string, unknown>;

/** How long after a screen's one read it answers the reads it stands for: long enough for the editor's renderer to load before its panels read. */
export const OPENING_MS = 60_000;

/** The backend's page when a list names none, and its largest (Page.cs). */
const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 500;

/**
 * What a write can change, as the parts of a screen read it ends:
 * - rows: the scene, its story and project, its placements, clips, keyframes, screen messages, materials and effects,
 *   the material presets and shader looks, and the library lists;
 * - content: the scene's content, which also carries its asset types' details, and its health;
 * - personal: the caller's guidance states and everything the session bootstrap holds.
 */
type Part = 'rows' | 'content' | 'personal';
const EVERY_PART: readonly Part[] = ['rows', 'content', 'personal'];

/** Writes that change no read: the client's sighting and a session refresh. */
const NOTHING_CHANGED = [/^\/api\/v1\/client-sightings$/, /^\/api\/v1\/sessions\/refresh$/];

/** Writes that change no scene row and no library list: guidance, lessons, notifications, feedback. */
const ROWS_UNCHANGED = [/^\/api\/v1\/guidance-/, /^\/api\/v1\/lesson-runs/, /^\/api\/v1\/notifications/, /^\/api\/v1\/user-feedback/, /^\/api\/v1\/bug-reports/];

/** Recording a model's animations (upsertClipTypes, as the renderer reports them while the scene opens): only the content's asset type details show it. */
const CLIP_TYPES = /^\/api\/v1\/clip-types(\/|$)/;

function endedBy(path: string): readonly Part[] {
  if (NOTHING_CHANGED.some(pattern => pattern.test(path))) return [];
  if (ROWS_UNCHANGED.some(pattern => pattern.test(path))) return ['personal'];
  if (CLIP_TYPES.test(path)) return ['content', 'personal'];
  return EVERY_PART;
}

/** A scene PIN or share link opens one scene to whoever holds it: such a read is always its own. */
const CAPABILITY_HEADERS = ['X-Lureo-Scene-Pin', 'X-Lureo-Share-Link'];

/** The reads the session bootstrap stands for. */
const SESSION_PATH = /^\/api\/v1\/(users\/me|orgs\/me|orgs\/me\/member|environment-types\/[^/]+|remote-config|version)$/;

/** The reads the editor workspace may answer; which of them it does is decided once it is read. */
const EDITOR_PATH = /^\/api\/v1\/(scenes\/[^/]+(\/(content|vfx|health))?|stories\/[^/]+|projects\/[^/]+|publishing-matrix|shader-looks|material-types|guidance-states|material-instances|asset-instances|clip-instances|keyframes|ui-element-instances|asset-types|asset-types\/library|ui-element-types)$/;

/** The lists the content holds, by the field it holds each in. */
const CONTENT_LISTS = {
  'asset-instances': 'assetInstances',
  'clip-instances': 'clipInstances',
  keyframes: 'keyframes',
  'ui-element-instances': 'uiElementInstances',
} as const satisfies Record<string, keyof SceneContent>;
type ContentList = keyof typeof CONTENT_LISTS;

/** The parameters a list answered from the content may carry. */
const CONTENT_FILTERS = new Set(['sceneUniqueId', 'assetInstanceUniqueId', 'category', 'isActive', 'limit', 'offset', 'sort']);

/** The library lists the editor preloads, by the categories each holds (EditorWorkspaceController). */
const LIBRARY_CATEGORIES = new Set(['prop', 'character', 'video']);
const PUBLIC_LIBRARY_CATEGORIES = new Set(['audio', 'image', 'image_anchor']);

/** One read a screen opens with, and the parts of it a write has ended since. */
interface Held<T> {
  at: number;
  /** The signed-in user it was read for. */
  userId: string | null;
  answer: Promise<T | null>;
  read?: T | null;
  ended: Set<Part>;
}

interface Read {
  path: string;
  /** The path's segments after /api/v1/, decoded. */
  segments: string[];
  params: URLSearchParams;
}

interface Found {
  part: Part;
  body: unknown;
}

const json = (body: unknown) => new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } });

const decode = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/** Whether the read names no parameter but these and paging. */
const only = (params: URLSearchParams, ...names: string[]) =>
  [...params.keys()].every(name => name === 'limit' || name === 'offset' || names.includes(name));

/** The one value of a parameter named once, else null. */
const single = (params: URLSearchParams, name: string) => {
  const values = params.getAll(name);
  return values.length === 1 ? values[0] : null;
};

/** One page of `rows`, as the backend pages a list; null when the paging is not one it would take. */
function page(rows: readonly unknown[], params: URLSearchParams): unknown {
  const offset = Number(params.get('offset') ?? 0);
  const limit = Math.min(Math.max(Number(params.get('limit') ?? DEFAULT_LIMIT), 1), MAX_LIMIT);
  if (!Number.isInteger(offset) || offset < 0 || !Number.isInteger(limit)) return null;
  return { items: rows.slice(offset, offset + limit), nextOffset: offset + limit < rows.length ? offset + limit : null, total: rows.length };
}

const listed = (part: Part, rows: readonly unknown[], params: URLSearchParams): Found | null => {
  const body = page(rows, params);
  return body === null ? null : { part, body };
};

/** A list of the scene's placements, clips, keyframes or screen messages from its content, as the server would answer it. */
function contentList(content: SceneContent, collection: ContentList, params: URLSearchParams): Found | null {
  if (![...params.keys()].every(name => CONTENT_FILTERS.has(name))) return null;
  if (params.getAll('sceneUniqueId').length > 1) return null;
  // isActive is answered only where every row says it (a screen message)
  if (params.has('isActive') && collection !== 'ui-element-instances') return null;
  const categories = params.getAll('category');
  const assets = params.getAll('assetInstanceUniqueId');
  const active = params.get('isActive');
  let rows = (content[CONTENT_LISTS[collection]] as unknown as Row[]).filter(row =>
    (categories.length === 0 || categories.includes(String(row.category)))
    && (assets.length === 0 || assets.includes(String(row.assetInstanceUniqueId)))
    && (active === null || String(row.isActive) === active));
  const sort = params.get('sort');
  if (sort) {
    const field = sort.replace(/^-/, '');
    const sign = sort.startsWith('-') ? -1 : 1;
    // Sorted by a field the rows don't carry: the server's order is its own
    if (rows.some(row => !(field in row))) return null;
    rows = [...rows].sort((a, b) => {
      const [x, y] = [a[field], b[field]];
      if (x === y) return 0;
      if (x == null) return -sign;
      if (y == null) return sign;
      return (x < y ? -1 : 1) * sign;
    });
  }
  return listed('rows', rows, params);
}

/** What the editor workspace of `sceneId` answers for this read, as the read itself would answer it; null when it doesn't hold it. */
function fromWorkspace(workspace: EditorWorkspace, sceneId: string, userId: string | null, read: Read): Found | null {
  const { segments, params } = read;
  const [collection, id, sub] = segments;
  const none = [...params.keys()].length === 0;
  const projectId = workspace.scene.projectUniqueId;
  const library = workspace.library;

  switch (collection) {
    case 'scenes':
      if (id !== sceneId || !none) return null;
      if (sub === undefined) return { part: 'rows', body: workspace.scene };
      if (sub === 'content') return { part: 'content', body: workspace.content };
      if (sub === 'vfx') return { part: 'rows', body: workspace.vfx };
      return workspace.health ? { part: 'content', body: workspace.health } : null;
    case 'stories':
      return none && workspace.story && workspace.story.uniqueId === id ? { part: 'rows', body: workspace.story } : null;
    case 'projects':
      return none && workspace.project && workspace.project.uniqueId === id ? { part: 'rows', body: workspace.project } : null;
    case 'publishing-matrix':
      return none ? { part: 'rows', body: workspace.publishingMatrix } : null;
    case 'shader-looks':
      return only(params) ? listed('rows', workspace.shaderLooks, params) : null;
    case 'material-types':
      return only(params) ? listed('rows', workspace.materialTypes, params) : null;
    case 'guidance-states':
      return only(params) ? listed('personal', workspace.guidanceStates, params) : null;
    case 'material-instances': {
      if (single(params, 'sceneUniqueId') === sceneId && only(params, 'sceneUniqueId')) return listed('rows', workspace.materials, params);
      // A placement's materials, for placements of this scene
      const assets = params.getAll('assetInstanceUniqueId');
      const placed = new Set(workspace.content.assetInstances.map(row => row.uniqueId));
      if (assets.length === 0 || !only(params, 'assetInstanceUniqueId') || !assets.every(asset => placed.has(asset))) return null;
      return listed('rows', workspace.materials.filter(row => assets.includes(row.assetInstanceUniqueId)), params);
    }
    case 'asset-instances': {
      if (params.has('sceneUniqueId') || params.has('assetInstanceUniqueId')) return contentList(workspace.content, collection, params);
      const categories = params.getAll('category');
      if (single(params, 'projectUniqueId') === projectId && only(params, 'projectUniqueId', 'category'))
        return listed('rows', library.projectAssetInstances.filter(row => categories.length === 0 || categories.includes(String(row.category))), params);
      if (userId && single(params, 'userUniqueId') === userId && only(params, 'userUniqueId', 'category')) {
        const category = single(params, 'category');
        if (category === 'audio') return listed('rows', library.myAudio, params);
        if (category === 'image') return listed('rows', library.myImages, params);
      }
      return null;
    }
    case 'clip-instances':
    case 'keyframes':
      return contentList(workspace.content, collection, params);
    case 'ui-element-instances':
      if (params.has('sceneUniqueId')) return contentList(workspace.content, collection, params);
      return single(params, 'projectUniqueId') === projectId && params.get('isActive') === 'true' && only(params, 'projectUniqueId', 'isActive')
        ? listed('rows', library.projectUiElementInstances, params)
        : null;
    case 'asset-types': {
      if (id === 'library') {
        const categories = params.getAll('category');
        if (categories.length === 0) return null;
        const holding = (rows: typeof library.libraryAssetTypes) => listed('rows', rows.filter(row => categories.includes(String(row.category))), params);
        if (!params.has('availability') && only(params, 'category') && categories.every(c => LIBRARY_CATEGORIES.has(c))) return holding(library.libraryAssetTypes);
        if (params.get('availability') === 'public' && only(params, 'category', 'availability') && categories.every(c => PUBLIC_LIBRARY_CATEGORIES.has(c)))
          return holding(library.publicLibraryAssetTypes);
        return null;
      }
      if (id !== undefined) return null;
      if (single(params, 'owner') === 'me' && only(params, 'owner')) return listed('rows', library.myAssetTypes, params);
      // The types placed in the project, which the scene's own placements are among
      const ids = params.getAll('uniqueId');
      const placed = new Set(library.projectAssetInstances.map(row => row.assetTypeUniqueId));
      if (ids.length === 0 || !only(params, 'uniqueId') || !ids.every(type => placed.has(type))) return null;
      return listed('rows', library.projectAssetTypes.filter(row => ids.includes(row.uniqueId)), params);
    }
    case 'ui-element-types':
      if (id !== undefined || params.get('isActive') !== 'true') return null;
      if (single(params, 'owner') === 'me' && only(params, 'owner', 'isActive')) return listed('rows', library.myUiElementTypes, params);
      if (single(params, 'availability') === 'public' && only(params, 'availability', 'isActive')) return listed('rows', library.publicUiElementTypes, params);
      return null;
    default:
      return null;
  }
}

/** What the session bootstrap answers for this read; null when it holds nothing for it (a part whose own read is a 404). */
function fromBootstrap(bootstrap: SessionBootstrap, environment: string, read: Read): Found | null {
  if ([...read.params.keys()].length > 0) return null;
  const found = (body: unknown) => (body == null ? null : { part: 'personal' as const, body });
  switch (read.path) {
    case '/api/v1/users/me': return found(bootstrap.user);
    case '/api/v1/orgs/me/member': return found(bootstrap.member);
    case '/api/v1/orgs/me': return found(bootstrap.org);
    case '/api/v1/remote-config': return found(bootstrap.remoteConfig);
    case '/api/v1/version': return found(bootstrap.version);
  }
  return read.segments[0] === 'environment-types' && read.segments[1] === environment ? found(bootstrap.environmentType) : null;
}

export interface SharedReadsOptions {
  /** The signed-in user; null when signed out. */
  userId?: () => string | null;
  /** The deployment's environment, whose preferences the session bootstrap carries (getEnvironmentType). */
  environment?: () => string;
}

export interface SharedReads {
  /** Sends a request, answering a read from one on its way or from a screen's one read where it can. */
  fetch(request: Request): Promise<Response>;
  /** A read answered from a screen's one read, without sending anything else; null when none answers it. */
  answer(request: Request): Promise<Response | null>;
  /** The editor opens this scene: its next reads are answered from one read of its workspace. */
  opened(sceneId: string): void;
  /** The app starts for whoever is signed in: their next start-up reads are answered from one read of the session bootstrap. */
  sessionOpened(): void;
  /** Stops answering from what screens read so far: something changed elsewhere (another tab, Claude). */
  forgetContent(): void;
  /** The session may belong to someone else now (signed in or out): if so, nothing read for the last user answers anything. */
  sessionChanged(): void;
  /** A number that goes up with each write that could change a read, and each change from elsewhere. */
  epoch(): number;
}

export function sharedReads(send: Send, now: () => number = () => Date.now(), options: SharedReadsOptions = {}): SharedReads {
  const userId = options.userId ?? (() => null);
  const environment = options.environment ?? (() => 'production');
  const inFlight = new Map<string, Promise<Response>>();
  const workspaces = new Map<string, Held<EditorWorkspace>>();
  // Scenes the editor is opening, whose next read reads their workspace
  const opening = new Set<string>();
  let session: (Held<SessionBootstrap> & { environment: string }) | null = null;
  let sessionArmed = false;
  let changes = 0;
  // The user the reads held and on their way were made for; undefined until the first request
  let seenUser: string | null | undefined;

  /** Nothing read so far answers a read from now on: each one on its way, and every screen's one read. */
  function forgetEverything(): void {
    changes += 1;
    inFlight.clear();
    workspaces.clear();
    opening.clear();
    session = null;
  }

  // Another user (or none) since the last request: what was read for the last one is never shown to this one
  function checkUser(): void {
    const user = userId();
    if (seenUser !== undefined && user !== seenUser) forgetEverything();
    seenUser = user;
  }

  function shared(request: Request): Promise<Response> {
    const key = `${request.url} ${JSON.stringify([...request.headers])}`;
    let pending = inFlight.get(key);
    if (!pending) {
      pending = send(request);
      inFlight.set(key, pending);
      const done = () => void (inFlight.get(key) === pending && inFlight.delete(key));
      pending.then(done, done);
    }
    // Each caller reads its own copy of the one answer
    return pending.then(response => response.clone());
  }

  // Read for the user signed in now, and recently enough
  const fresh = (held: Held<unknown> | null | undefined) => !!held && held.userId === userId() && now() - held.at < OPENING_MS;

  /** The address of `path` on the backend `like` is addressed to. */
  function addressOf(like: URL, path: string, query?: Record<string, string>): string {
    const url = new URL(like);
    url.pathname = `${url.pathname.slice(0, url.pathname.indexOf('/api/v1/'))}${path}`;
    url.search = query ? `?${new URLSearchParams(query).toString()}` : '';
    return url.href;
  }

  function hold<T>(href: string, user: string | null, held: Held<T> = { at: now(), userId: user, answer: Promise.resolve(null), ended: new Set() }): Held<T> {
    held.answer = shared(new Request(href, { method: 'GET' }))
      .then(async response => (response.ok ? ((await response.json()) as T) : null))
      .catch(() => null)
      .then(read => (held.read = read));
    return held;
  }

  /** The scene's workspace as the editor opens it: read once per opening, or null when the scene isn't open. */
  function workspaceOf(sceneId: string, like: URL): Held<EditorWorkspace> | null {
    if (!opening.delete(sceneId)) {
      const existing = workspaces.get(sceneId);
      return existing && fresh(existing) ? existing : null;
    }
    const held = hold<EditorWorkspace>(addressOf(like, `/api/v1/scenes/${encodeURIComponent(sceneId)}/workspace`), userId());
    workspaces.set(sceneId, held);
    return held;
  }

  /** The open scene whose fresh workspace places every one of these, for a list by placement that names no scene. */
  function sceneHolding(assetIds: string[]): string | null {
    for (const [sceneId, held] of workspaces) {
      if (!fresh(held) || !held.read) continue;
      const placed = new Set(held.read.content.assetInstances.map(row => row.uniqueId));
      if (assetIds.every(id => placed.has(id))) return sceneId;
    }
    return null;
  }

  /** The scene a read is about: the one it names, the one placing what it names, else the one the editor is opening or has open. */
  function sceneOf(read: Read): string | null {
    if (read.segments[0] === 'scenes') return read.segments[1] ?? null;
    const scenes = read.params.getAll('sceneUniqueId');
    if (scenes.length > 0) return scenes.length === 1 ? scenes[0] : null;
    // The timeline reads each row's keyframes by its placement, with no scene named
    const assets = read.params.getAll('assetInstanceUniqueId');
    if (assets.length > 0) return sceneHolding(assets);
    const [pending] = opening;
    if (pending) return pending;
    let newest: [string, Held<EditorWorkspace>] | null = null;
    for (const entry of workspaces) if (fresh(entry[1]) && (!newest || entry[1].at > newest[1].at)) newest = entry;
    return newest?.[0] ?? null;
  }

  /** The signed-in user's session bootstrap as the app starts: read once per start, or null when the app isn't starting for them. */
  function sessionOf(like: URL): (Held<SessionBootstrap> & { environment: string }) | null {
    const user = userId();
    if (!user) return null;
    if (session && session.userId === user && fresh(session)) return session;
    if (!sessionArmed) return null;
    sessionArmed = false;
    const named = environment();
    const held: Held<SessionBootstrap> & { environment: string } = { at: now(), userId: user, answer: Promise.resolve(null), ended: new Set(), environment: named };
    session = held;
    hold(addressOf(like, '/api/v1/users/me/bootstrap', { environment: named }), user, held);
    return held;
  }

  const usable = (held: Held<unknown>, part: Part) => fresh(held) && !held.ended.has(part);

  async function fromOpening(request: Request): Promise<Response | null> {
    if (request.method !== 'GET' || CAPABILITY_HEADERS.some(name => request.headers.has(name))) return null;
    const url = new URL(request.url);
    const path = apiPath(url.href);
    if (!path) return null;
    const read: Read = { path, segments: path.split('/').slice(3).map(decode), params: url.searchParams };

    if (SESSION_PATH.test(path)) {
      const held = sessionOf(url);
      if (!held) return null;
      const bootstrap = await held.answer;
      // Signed out or in as someone else meanwhile, or written since: read as it is now
      if (!bootstrap || session !== held || held.userId !== userId()) return null;
      const found = fromBootstrap(bootstrap, held.environment, read);
      return found && usable(held, found.part) ? json(found.body) : null;
    }

    if (!EDITOR_PATH.test(path)) return null;
    const sceneId = sceneOf(read);
    if (!sceneId) return null;
    const held = workspaceOf(sceneId, url);
    if (!held) return null;
    const workspace = await held.answer;
    // Signed out or in as someone else meanwhile, or opened again since: read as it is now
    if (!workspace || workspaces.get(sceneId) !== held || held.userId !== userId()) return null;
    const found = fromWorkspace(workspace, sceneId, held.userId, read);
    return found && usable(held, found.part) ? json(found.body) : null;
  }

  // A write that could change a read ends what the screens read, and a read sent after it never shares one sent before
  function wrote(path: string) {
    const parts = endedBy(path);
    if (parts.length === 0) return;
    changes += 1;
    inFlight.clear();
    for (const held of [...workspaces.values(), ...(session ? [session] : [])]) for (const part of parts) held.ended.add(part);
  }

  return {
    async fetch(request) {
      checkUser();
      if (request.method !== 'GET') {
        const path = apiPath(request.url) ?? '';
        wrote(path);
        // A read sent while it was on its way may have been answered before it landed: once it has, that read is shared no more
        const landed = () => wrote(path);
        const answer = send(request);
        answer.then(landed, landed);
        return answer;
      }
      // A stream stays the caller's own
      if (changeStream(request.url)) return send(request);
      const opened = await fromOpening(request).catch(() => null);
      return opened ?? shared(request);
    },
    answer(request) {
      checkUser();
      return fromOpening(request).catch(() => null);
    },
    opened(sceneId) {
      opening.add(sceneId);
    },
    sessionOpened() {
      // A new start: what an earlier one read answers nothing now
      session = null;
      sessionArmed = true;
    },
    forgetContent() {
      changes += 1;
      inFlight.clear();
      workspaces.clear();
      session = null;
    },
    sessionChanged() {
      checkUser();
    },
    epoch() {
      return changes;
    },
  };
}
